/**
 * chartRegistry.js
 * Central mapping of chart keys to components, shared by ExportRenderer and ChartSelectionModal.
 */

// Overview charts
import { TocDiagram } from './overview/TocDiagram';
import { KpiStrip } from './overview/KpiStrip';
import { ResultTreemapChart } from './overview/ResultTreemapChart';
import { ImpactAreaRadarChart } from './overview/ImpactAreaRadarChart';
import { ExecutiveSankeyDiagram } from './overview/ExecutiveSankeyDiagram';
import { ProgressAgainstTargetsTable } from './overview/ProgressAgainstTargetsTable';
import { HighLevelTocDiagram } from './overview/HighLevelTocDiagram';
import { ExecutiveSpheresDiagram } from './overview/ExecutiveSpheresDiagram';
import { ExecutivePetalsDiagram } from './overview/ExecutivePetalsDiagram';
import OverviewDiagram from '../OverviewDiagram';

// Progress charts
import { WorldMapChart } from './progress/WorldMapChart';
import { ImpactAreaChart } from './progress/ImpactAreaChart';
import { ResultsByTypeAndFundingChart } from './progress/ResultsByTypeAndFundingChart';
import { TopCountriesChart } from './progress/TopCountriesChart';
import { RegionsChart } from './progress/RegionsChart';
import { InnovationPipelineChart } from './progress/InnovationPipelineChart';
import { AowTrajectoryPreview } from './progress/AowTrajectoryChart';
import { AowTocFrameworkPreview } from './overview/AowTocFrameworkDiagram';
import { AowTocFrameworkDetailPreview } from './overview/AowTocFrameworkDetailDiagram';

// Shared charts
import { ResultsLevelChart } from './shared/ResultsLevelChart';

// Partnership charts
import { TopPartnersChart } from './partnership/TopPartnersChart';
import { PartnerFlowChart } from './partnership/PartnerFlowChart';
import { PortfolioLinkageChart } from './partnership/PortfolioLinkageChart';
import { PartnershipMapChart } from './partnership/PartnershipMapChart';
import { ContributingCentersChart } from './partnership/ContributingCentersChart';
import { W3ProjectsChart } from './partnership/W3ProjectsChart';
import { FunderDistributionChart } from './partnership/FunderDistributionChart';
import { CrossProgramChart } from './partnership/CrossProgramChart';
import { PartnerCoverageCallout } from './partnership/PartnerCoverageCallout';
import { PartnerProgramNetwork } from './partnership/PartnerProgramNetwork';
import { PartnerRadialChart } from './partnership/PartnerRadialChart';
import { PartnerLandscapeChart } from './partnership/PartnerLandscapeChart';

// Innovation charts
import { InnovationTypeBreakdownChart } from './innovation/InnovationTypeBreakdownChart';
import { InnovationUseLevelChart } from './innovation/InnovationUseLevelChart';
import { InnovationReadinessLevelChart } from './innovation/InnovationReadinessLevelChart';
import { InnovationGeographyChart } from './innovation/InnovationGeographyChart';
import { InnovationCrossProgramChart } from './innovation/InnovationCrossProgramChart';
import { InnovationNatureChart } from './innovation/InnovationNatureChart';
import { InnovationEvolutionChart } from './innovation/InnovationEvolutionChart';
import { InnovationLinkageChart } from './innovation/InnovationLinkageChart';
import { InnovationPackageChart } from './innovation/InnovationPackageChart';
import { InnovationUsersChart } from './innovation/InnovationUsersChart';
import { DiscontinuedInnovationsChart } from './innovation/DiscontinuedInnovationsChart';
import { InnovationImpactAreaChart } from './innovation/InnovationImpactAreaChart';
import { InnovationPartnerChart } from './innovation/InnovationPartnerChart';
import { InnovationCollaborationChart } from './innovation/InnovationCollaborationChart';

export const OVERVIEW_TABS = {
  kpi: KpiStrip,
  sankey: ExecutiveSankeyDiagram,
  targets: ProgressAgainstTargetsTable,
  overview: OverviewDiagram,
  tocOverview: HighLevelTocDiagram,
  treemap: ResultTreemapChart,
  toc: TocDiagram,
  impact: ImpactAreaRadarChart,
};

export const PROGRESS_TABS = {
  map: WorldMapChart,
  byTypeFunding: ResultsByTypeAndFundingChart,
  impact: ImpactAreaChart,
  countries: TopCountriesChart,
  regions: RegionsChart,
  byLevel: ResultsLevelChart,
  innovation: InnovationPipelineChart,
  spheres: ExecutiveSpheresDiagram,
  petals: ExecutivePetalsDiagram,
  aowFramework: AowTocFrameworkPreview,
  aowFrameworkDetail: AowTocFrameworkDetailPreview,
  aowTrajectory: AowTrajectoryPreview,
};

export const PARTNERSHIP_TABS = {
  partnerFlow: PartnerFlowChart,
  partnerMap: PartnershipMapChart,
  landscape: PartnerLandscapeChart,
  linkages: PortfolioLinkageChart,
  topPartners: TopPartnersChart,
  coverage: PartnerCoverageCallout,
  network: PartnerProgramNetwork,
  radial: PartnerRadialChart,
  centers: ContributingCentersChart,
  w3Projects: W3ProjectsChart,
  funders: FunderDistributionChart,
  crossProgram: CrossProgramChart,
};

export const INNOVATION_TABS = {
  type: InnovationTypeBreakdownChart,
  useLevel: InnovationUseLevelChart,
  readiness: InnovationReadinessLevelChart,
  geography: InnovationGeographyChart,
  crossProgram: InnovationCrossProgramChart,
  nature: InnovationNatureChart,
  evolution: InnovationEvolutionChart,
  linkages: InnovationLinkageChart,
  packages: InnovationPackageChart,
  users: InnovationUsersChart,
  discontinued: DiscontinuedInnovationsChart,
  impact: InnovationImpactAreaChart,
  partners: InnovationPartnerChart,
  collaboration: InnovationCollaborationChart,
};

// Portfolio-specific charts
import { ProgramBreakdownChart } from './portfolio/ProgramBreakdownChart';
import { ImpactAreaSummaryChart } from './portfolio/ImpactAreaSummaryChart';
import { PortfolioKpiStrip } from './portfolio/PortfolioKpiStrip';
import { CrossProgramHeatmap } from './portfolio/CrossProgramHeatmap';
import { GenderLevelChart, ClimateLevelChart, NutritionLevelChart, BiodiversityLevelChart, PovertyLevelChart } from './portfolio/ImpactAreaLevelCharts';
import {
  GenderByProgramChart, ClimateByProgramChart, NutritionByProgramChart, BiodiversityByProgramChart, PovertyByProgramChart,
  GenderChordChart, ClimateChordChart, NutritionChordChart, BiodiversityChordChart, PovertyChordChart,
  GenderGeoMapChart, ClimateGeoMapChart, NutritionGeoMapChart, BiodiversityGeoMapChart, PovertyGeoMapChart,
  GenderByTypeChart, ClimateByTypeChart, NutritionByTypeChart, BiodiversityByTypeChart, PovertyByTypeChart,
  GenderFundingChart, ClimateFundingChart, NutritionFundingChart, BiodiversityFundingChart, PovertyFundingChart,
  GenderHeatmapChart, ClimateHeatmapChart, NutritionHeatmapChart, BiodiversityHeatmapChart, PovertyHeatmapChart,
  GenderSynergiesChart, ClimateSynergiesChart, NutritionSynergiesChart, BiodiversitySynergiesChart, PovertySynergiesChart,
} from './portfolio/ImpactAreaExportCharts';
import { AfricaBarChart, AmericasBarChart, AsiaBarChart, AfricaMapChart, AmericasMapChart, AsiaMapChart } from './portfolio/RegionalExportCharts';
import {
  AfricaByProgramChart, AmericasByProgramChart, AsiaByProgramChart,
  AfricaChordChart, AmericasChordChart, AsiaChordChart,
  AfricaByTypeChart, AmericasByTypeChart, AsiaByTypeChart,
  AfricaFundingChart, AmericasFundingChart, AsiaFundingChart,
  AfricaHeatmapChart, AmericasHeatmapChart, AsiaHeatmapChart,
  AfricaImpactAreaChart, AmericasImpactAreaChart, AsiaImpactAreaChart,
} from './portfolio/RegionalExportAdditionalCharts';

export const PORTFOLIO_OVERVIEW_TABS = {
  programBreakdown: ProgramBreakdownChart,
  kpi: PortfolioKpiStrip,
  impactAreas: ImpactAreaSummaryChart,
  crossProgram: CrossProgramHeatmap,
};

export const PORTFOLIO_IMPACT_AREA_TABS = {
  // Level Distribution
  iaGender: GenderLevelChart,
  iaClimate: ClimateLevelChart,
  iaNutrition: NutritionLevelChart,
  iaBiodiversity: BiodiversityLevelChart,
  iaPoverty: PovertyLevelChart,
  // Geographic Map
  iaGenderGeoMap: GenderGeoMapChart,
  iaClimateGeoMap: ClimateGeoMapChart,
  iaNutritionGeoMap: NutritionGeoMapChart,
  iaBiodiversityGeoMap: BiodiversityGeoMapChart,
  iaPovertyGeoMap: PovertyGeoMapChart,
  // By Program
  iaGenderProgram: GenderByProgramChart,
  iaClimateProgram: ClimateByProgramChart,
  iaNutritionProgram: NutritionByProgramChart,
  iaBiodiversityProgram: BiodiversityByProgramChart,
  iaPovertyProgram: PovertyByProgramChart,
  // By Type
  iaGenderByType: GenderByTypeChart,
  iaClimateByType: ClimateByTypeChart,
  iaNutritionByType: NutritionByTypeChart,
  iaBiodiversityByType: BiodiversityByTypeChart,
  iaPovertyByType: PovertyByTypeChart,
  // Funding Split
  iaGenderFunding: GenderFundingChart,
  iaClimateFunding: ClimateFundingChart,
  iaNutritionFunding: NutritionFundingChart,
  iaBiodiversityFunding: BiodiversityFundingChart,
  iaPovertyFunding: PovertyFundingChart,
  // Chord
  iaGenderChord: GenderChordChart,
  iaClimateChord: ClimateChordChart,
  iaNutritionChord: NutritionChordChart,
  iaBiodiversityChord: BiodiversityChordChart,
  iaPovertyChord: PovertyChordChart,
  // Heatmap
  iaGenderHeatmap: GenderHeatmapChart,
  iaClimateHeatmap: ClimateHeatmapChart,
  iaNutritionHeatmap: NutritionHeatmapChart,
  iaBiodiversityHeatmap: BiodiversityHeatmapChart,
  iaPovertyHeatmap: PovertyHeatmapChart,
  // Synergies
  iaGenderSynergies: GenderSynergiesChart,
  iaClimateSynergies: ClimateSynergiesChart,
  iaNutritionSynergies: NutritionSynergiesChart,
  iaBiodiversitySynergies: BiodiversitySynergiesChart,
  iaPovertySynergies: PovertySynergiesChart,
};

export const PORTFOLIO_REGIONAL_TABS = {
  // Top Countries
  africaBar: AfricaBarChart,
  americasBar: AmericasBarChart,
  asiaBar: AsiaBarChart,
  // Geographic Map
  africaMap: AfricaMapChart,
  americasMap: AmericasMapChart,
  asiaMap: AsiaMapChart,
  // By Program
  africaProgram: AfricaByProgramChart,
  americasProgram: AmericasByProgramChart,
  asiaProgram: AsiaByProgramChart,
  // By Type
  africaByType: AfricaByTypeChart,
  americasByType: AmericasByTypeChart,
  asiaByType: AsiaByTypeChart,
  // Funding Split
  africaFunding: AfricaFundingChart,
  americasFunding: AmericasFundingChart,
  asiaFunding: AsiaFundingChart,
  // Chord
  africaChord: AfricaChordChart,
  americasChord: AmericasChordChart,
  asiaChord: AsiaChordChart,
  // Heatmap
  africaHeatmap: AfricaHeatmapChart,
  americasHeatmap: AmericasHeatmapChart,
  asiaHeatmap: AsiaHeatmapChart,
  // Impact Area Coverage
  africaImpactArea: AfricaImpactAreaChart,
  americasImpactArea: AmericasImpactAreaChart,
  asiaImpactArea: AsiaImpactAreaChart,
};

export const PORTFOLIO_PARTNERSHIPS_TABS = {
  partnerFlow: PartnerFlowChart,
  topPartners: TopPartnersChart,
  partnerMap: PartnershipMapChart,
  crossProgram: CrossProgramChart,
};

export const SECTION_TAB_MAP = {
  overview: OVERVIEW_TABS,
  progress: PROGRESS_TABS,
  partnerships: PARTNERSHIP_TABS,
  innovation: INNOVATION_TABS,
  'portfolio-overview': PORTFOLIO_OVERVIEW_TABS,
  'portfolio-impact-areas': PORTFOLIO_IMPACT_AREA_TABS,
  'portfolio-regional': PORTFOLIO_REGIONAL_TABS,
  'portfolio-partnerships': PORTFOLIO_PARTNERSHIPS_TABS,
};
